// API for user/contact list
import { User } from '../contexts/UserContext';
import { getApiUrl } from './api.config';

const readApiError = async (response: Response): Promise<string | undefined> => {
  try {
    const data = await response.json();
    return data?.error;
  } catch {
    try {
      const text = await response.text();
      if (text?.trim().startsWith('<')) {
        return 'API returned HTML (check API base URL / proxy configuration)';
      }
      return text;
    } catch {
      return undefined;
    }
  }
};

export interface DeletionFeedback {
  name?: string;
  email?: string;
  reason: string;
  message?: string;
}

export const userApi = {
  getContacts: async (): Promise<User[]> => {
    const token = localStorage.getItem('authToken');
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(getApiUrl('Contacts'), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch contacts');
    return response.json();
  },

  /**
   * Delete user account and all associated data
   */
  deleteAccount: async (email: string): Promise<{ message: string; success: boolean }> => {
    const token = localStorage.getItem('authToken');
    if (!token) throw new Error('Not authenticated');
    
    const url = `${getApiUrl('users/delete')}?email=${encodeURIComponent(email)}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await readApiError(response);
      throw new Error(error || 'Failed to delete account');
    }

    return response.json();
  },

  /**
   * Admin-only: delete any user account by email
   */
  adminDeleteUser: async (email: string): Promise<{ message: string; success: boolean }> => {
    const token = localStorage.getItem('authToken');
    if (!token) throw new Error('Not authenticated');

    const url = `${getApiUrl('users/admin/delete')}?email=${encodeURIComponent(email)}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await readApiError(response);
      throw new Error(error || 'Failed to delete user');
    }

    return response.json();
  },

  /**
   * Admin-only: grant premium to the owner account
   */
  adminGrantPremium: async (): Promise<{ message: string; success: boolean }> => {
    const token = localStorage.getItem('authToken');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(getApiUrl('users/admin/grant-premium'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await readApiError(response);
      throw new Error(error || 'Failed to grant premium');
    }

    return response.json();
  },

  /**
   * Admin-only: revoke premium from the owner account
   */
  adminRevokePremium: async (): Promise<{ message: string; success: boolean }> => {
    const token = localStorage.getItem('authToken');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(getApiUrl('users/admin/revoke-premium'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await readApiError(response);
      throw new Error(error || 'Failed to revoke premium');
    }

    return response.json();
  },

  /**
   * Submit feedback after account deletion
   */
  submitDeletionFeedback: async (feedback: DeletionFeedback): Promise<{ message: string; success: boolean }> => {
    const response = await fetch(getApiUrl('users/feedback'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(feedback),
    });

    if (!response.ok) {
      const error = await readApiError(response);
      throw new Error(error || 'Failed to submit feedback');
    }

    return response.json();
  },
};
